import { useState, useEffect } from 'react';
import { Download, Users, Home as HomeIcon, MessageSquare, TrendingUp, MapPin, Building2, Plus, RefreshCw, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#064E3B', '#D97706', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899'];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [properties, setProperties] = useState([]);
  const [builderProjects, setBuilderProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [showAddProjectForm, setShowAddProjectForm] = useState(false);
  const [newProject, setNewProject] = useState({
    builder_name: '',
    project_name: '',
    district: '',
    city: '',
    configurations: '',
    price_min: '',
    price_max: '',
    launch_date: '',
    possession_date: '',
    amenities: '',
    rera_number: '',
    status: 'upcoming',
    images: '',
    description: '',
    total_units: '',
    available_units: '',
    source_url: '',
    approved: false
  });

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      const [statsRes, propertiesRes, builderProjectsRes, usersRes, inquiriesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/properties`, { withCredentials: true }),
        axios.get(`${API}/admin/builder-projects?include_unapproved=true`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/inquiries`, { withCredentials: true })
      ]);
      
      setStats(statsRes.data);
      setProperties(propertiesRes.data);
      setBuilderProjects(builderProjectsRes.data);
      setUsers(usersRes.data);
      setInquiries(inquiriesRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = Object.keys(data[0]).filter(key => key !== 'password_hash');
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          if (typeof value === 'object') return JSON.stringify(value).replace(/,/g, ';');
          return `"${value}"`;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success(`${filename} exported successfully!`);
  };

  const exportToJSON = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    toast.success(`${filename} exported successfully!`);
  };

  const handleAddProject = async (e) => {
    e.preventDefault();
    
    try {
      const projectData = {
        builder_name: newProject.builder_name,
        project_name: newProject.project_name,
        location: {
          district: newProject.district,
          city: newProject.city,
          state: 'Kerala'
        },
        configurations: newProject.configurations.split(',').map(c => c.trim()),
        price_range: {
          min: parseFloat(newProject.price_min) || 0,
          max: parseFloat(newProject.price_max) || 0
        },
        launch_date: newProject.launch_date || null,
        possession_date: newProject.possession_date || null,
        amenities: newProject.amenities ? newProject.amenities.split(',').map(a => a.trim()) : [],
        rera_number: newProject.rera_number || null,
        status: newProject.status,
        images: newProject.images ? newProject.images.split(',').map(i => i.trim()) : [],
        description: newProject.description,
        total_units: newProject.total_units ? parseInt(newProject.total_units) : null,
        available_units: newProject.available_units ? parseInt(newProject.available_units) : null,
        source_url: newProject.source_url,
        approved: newProject.approved
      };

      await axios.post(`${API}/admin/builder-projects/create`, projectData, { withCredentials: true });
      
      toast.success('Builder project added successfully!');
      setShowAddProjectForm(false);
      setNewProject({
        builder_name: '', project_name: '', district: '', city: '', configurations: '',
        price_min: '', price_max: '', launch_date: '', possession_date: '', amenities: '',
        rera_number: '', status: 'upcoming', images: '', description: '', total_units: '',
        available_units: '', source_url: '', approved: false
      });
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to add project');
    }
  };

  const handleApproveProject = async (projectId) => {
    try {
      await axios.post(`${API}/admin/builder-projects/${projectId}/approve`, {}, { withCredentials: true });
      toast.success('Project approved!');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to approve project');
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    
    try {
      await axios.delete(`${API}/admin/builder-projects/${projectId}`, { withCredentials: true });
      toast.success('Project deleted!');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const handleRunScraper = async () => {
    setScraping(true);
    try {
      toast.info('Starting scraper... This may take a few minutes.');
      const response = await axios.post(`${API}/builder-projects/scrape`, {}, { 
        withCredentials: true,
        timeout: 300000 // 5 minute timeout
      });
      
      const results = response.data.results;
      toast.success(
        `Scraping complete! Found ${results.total_scraped} projects. ` +
        `${results.new_saved} new, ${results.updated} updated.`
      );
      fetchAdminData();
    } catch (error) {
      toast.error('Scraper failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setScraping(false);
    }
  };

  const handleBulkApprove = async () => {
    const pendingProjects = builderProjects.filter(p => !p.approved);
    if (pendingProjects.length === 0) {
      toast.info('No pending projects to approve');
      return;
    }
    
    if (!window.confirm(`Approve all ${pendingProjects.length} pending projects?`)) return;
    
    try {
      const projectIds = pendingProjects.map(p => p.project_id);
      await axios.post(`${API}/admin/builder-projects/bulk-approve`, { project_ids: projectIds }, { withCredentials: true });
      toast.success(`Approved ${pendingProjects.length} projects!`);
      fetchAdminData();
    } catch (error) {
      toast.error('Bulk approve failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-12">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-2" data-testid="admin-heading">
            Admin Dashboard
          </h1>
          <p className="text-lg text-slate-600">Manage and analyze your real estate platform</p>
        </div>

        {/* Stats Overview */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg border border-slate-100 shadow-sm" data-testid="stat-properties">
            <div className="flex items-center justify-between mb-2">
              <HomeIcon className="w-8 h-8 text-primary" />
              <span className="text-3xl font-bold text-primary">{stats?.total_properties || 0}</span>
            </div>
            <p className="text-slate-600 font-medium">Total Properties</p>
            <p className="text-sm text-slate-500">{stats?.active_properties || 0} active listings</p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-slate-100 shadow-sm" data-testid="stat-users">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-8 h-8 text-secondary" />
              <span className="text-3xl font-bold text-secondary">{stats?.total_users || 0}</span>
            </div>
            <p className="text-slate-600 font-medium">Total Users</p>
            <p className="text-sm text-slate-500">Registered accounts</p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-slate-100 shadow-sm" data-testid="stat-inquiries">
            <div className="flex items-center justify-between mb-2">
              <MessageSquare className="w-8 h-8 text-accent" />
              <span className="text-3xl font-bold text-accent">{stats?.total_inquiries || 0}</span>
            </div>
            <p className="text-slate-600 font-medium">Total Inquiries</p>
            <p className="text-sm text-slate-500">Customer interest</p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-slate-100 shadow-sm" data-testid="stat-conversion">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 text-blue-500" />
              <span className="text-3xl font-bold text-blue-500">
                {stats?.total_properties > 0 ? ((stats.total_inquiries / stats.total_properties) * 100).toFixed(1) : 0}%
              </span>
            </div>
            <p className="text-slate-600 font-medium">Inquiry Rate</p>
            <p className="text-sm text-slate-500">Per property average</p>
          </div>
        </div>

        <Tabs defaultValue="analytics" className="w-full">
          <TabsList className="grid w-full max-w-3xl grid-cols-5 mb-8">
            <TabsTrigger value="analytics" data-testid="tab-analytics">
              <TrendingUp className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="properties" data-testid="tab-admin-properties">
              <HomeIcon className="w-4 h-4 mr-2" />
              Properties
            </TabsTrigger>
            <TabsTrigger value="builder-projects" data-testid="tab-builder-projects">
              <Building2 className="w-4 h-4 mr-2" />
              Builder Projects
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-admin-users">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="tab-admin-inquiries">
              <MessageSquare className="w-4 h-4 mr-2" />
              Inquiries
            </TabsTrigger>
          </TabsList>

          {/* Analytics Tab */}
          <TabsContent value="analytics" data-testid="analytics-content">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Properties by District */}
              <div className="bg-white p-6 rounded-lg border border-slate-100 shadow-sm">
                <h3 className="text-xl font-semibold mb-4">Properties by District</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stats?.district_stats || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="district" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#064E3B" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Properties by Type */}
              <div className="bg-white p-6 rounded-lg border border-slate-100 shadow-sm">
                <h3 className="text-xl font-semibold mb-4">Property Types</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={stats?.type_stats || []}
                      dataKey="count"
                      nameKey="type"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label
                    >
                      {(stats?.type_stats || []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Listing Types */}
              <div className="bg-white p-6 rounded-lg border border-slate-100 shadow-sm">
                <h3 className="text-xl font-semibold mb-4">Sale vs Rent</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={stats?.listing_stats || []}
                      dataKey="count"
                      nameKey="listing_type"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label
                    >
                      {(stats?.listing_stats || []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Top Districts */}
              <div className="bg-white p-6 rounded-lg border border-slate-100 shadow-sm">
                <h3 className="text-xl font-semibold mb-4">Top Districts</h3>
                <div className="space-y-3">
                  {(stats?.district_stats || [])
                    .sort((a, b) => b.count - a.count)
                    .slice(0, 5)
                    .map((district, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded">
                        <div className="flex items-center gap-3">
                          <MapPin className="w-5 h-5 text-primary" />
                          <span className="font-medium">{district.district}</span>
                        </div>
                        <span className="text-lg font-bold text-primary">{district.count}</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Properties Tab */}
          <TabsContent value="properties" data-testid="properties-content">
            <div className="bg-white rounded-lg border border-slate-100 shadow-sm">
              <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                <h3 className="text-xl font-semibold">All Properties ({properties.length})</h3>
                <div className="flex gap-2">
                  <Button
                    data-testid="export-properties-csv"
                    onClick={() => exportToCSV(properties, 'properties')}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Export CSV
                  </Button>
                  <Button
                    data-testid="export-properties-json"
                    onClick={() => exportToJSON(properties, 'properties')}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Export JSON
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="text-left p-4 font-semibold">Property ID</th>
                      <th className="text-left p-4 font-semibold">Title</th>
                      <th className="text-left p-4 font-semibold">Type</th>
                      <th className="text-left p-4 font-semibold">Listing</th>
                      <th className="text-left p-4 font-semibold">Price (₹)</th>
                      <th className="text-left p-4 font-semibold">District</th>
                      <th className="text-left p-4 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {properties.map((property) => (
                      <tr key={property.property_id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-4 text-sm font-mono">{property.property_id}</td>
                        <td className="p-4">{property.title}</td>
                        <td className="p-4 capitalize">{property.property_type}</td>
                        <td className="p-4 capitalize">{property.listing_type}</td>
                        <td className="p-4 font-semibold">₹{property.price?.toLocaleString('en-IN')}</td>
                        <td className="p-4">{property.location?.district}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            property.status === 'available' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'
                          }`}>
                            {property.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Builder Projects Tab */}
          <TabsContent value="builder-projects" data-testid="builder-projects-content">
            {/* Scraper Control Section */}
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg border border-slate-200 p-6 mb-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold mb-1">Builder Project Scraper</h3>
                  <p className="text-sm text-slate-600">
                    Automatically fetch new projects from 15 Kerala builders. 
                    Pending: <span className="font-semibold text-yellow-600">{builderProjects.filter(p => !p.approved).length}</span> | 
                    Approved: <span className="font-semibold text-green-600">{builderProjects.filter(p => p.approved).length}</span>
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button
                    data-testid="run-scraper-btn"
                    onClick={handleRunScraper}
                    disabled={scraping}
                    className="bg-secondary hover:bg-secondary/90 text-white flex items-center gap-2"
                  >
                    <RefreshCw className={`w-4 h-4 ${scraping ? 'animate-spin' : ''}`} />
                    {scraping ? 'Scraping...' : 'Run Scraper'}
                  </Button>
                  <Button
                    data-testid="bulk-approve-btn"
                    onClick={handleBulkApprove}
                    disabled={builderProjects.filter(p => !p.approved).length === 0}
                    className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Approve All Pending
                  </Button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-100 shadow-sm mb-6">
              <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                <h3 className="text-xl font-semibold">Builder Projects ({builderProjects.length})</h3>
                <div className="flex gap-2">
                  <Button
                    data-testid="add-project-button"
                    onClick={() => setShowAddProjectForm(!showAddProjectForm)}
                    className="bg-primary hover:bg-primary/90 text-white flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Project
                  </Button>
                  <Button
                    data-testid="export-builder-projects-csv"
                    onClick={() => exportToCSV(builderProjects, 'builder_projects')}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Export CSV
                  </Button>
                </div>
              </div>

              {/* Add Project Form */}
              {showAddProjectForm && (
                <div className="p-6 bg-slate-50 border-b border-slate-100">
                  <h4 className="text-lg font-semibold mb-4">Add New Builder Project</h4>
                  <form onSubmit={handleAddProject} className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Builder Name *</label>
                      <input
                        type="text"
                        required
                        className="w-full px-3 py-2 border border-slate-200 rounded"
                        value={newProject.builder_name}
                        onChange={(e) => setNewProject({...newProject, builder_name: e.target.value})}
                        placeholder="e.g., Sobha Ltd"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Project Name *</label>
                      <input
                        type="text"
                        required
                        className="w-full px-3 py-2 border border-slate-200 rounded"
                        value={newProject.project_name}
                        onChange={(e) => setNewProject({...newProject, project_name: e.target.value})}
                        placeholder="e.g., Sobha Dream Acres"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">District *</label>
                      <select
                        required
                        className="w-full px-3 py-2 border border-slate-200 rounded"
                        value={newProject.district}
                        onChange={(e) => setNewProject({...newProject, district: e.target.value})}
                      >
                        <option value="">Select District</option>
                        {['Ernakulam', 'Kozhikode', 'Thrissur', 'Thiruvananthapuram', 'Kottayam', 'Kollam', 'Palakkad', 'Malappuram', 'Kannur', 'Kasaragod', 'Alappuzha', 'Pathanamthitta', 'Idukki', 'Wayanad'].map(d => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">City</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-slate-200 rounded"
                        value={newProject.city}
                        onChange={(e) => setNewProject({...newProject, city: e.target.value})}
                        placeholder="e.g., Kochi"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Configurations (comma separated)</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-slate-200 rounded"
                        value={newProject.configurations}
                        onChange={(e) => setNewProject({...newProject, configurations: e.target.value})}
                        placeholder="e.g., 2 BHK, 3 BHK, 4 BHK"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Status</label>
                      <select
                        className="w-full px-3 py-2 border border-slate-200 rounded"
                        value={newProject.status}
                        onChange={(e) => setNewProject({...newProject, status: e.target.value})}
                      >
                        <option value="upcoming">Upcoming</option>
                        <option value="open for booking">Open for Booking</option>
                        <option value="sold out">Sold Out</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Price Min (₹)</label>
                      <input
                        type="number"
                        className="w-full px-3 py-2 border border-slate-200 rounded"
                        value={newProject.price_min}
                        onChange={(e) => setNewProject({...newProject, price_min: e.target.value})}
                        placeholder="e.g., 7500000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Price Max (₹)</label>
                      <input
                        type="number"
                        className="w-full px-3 py-2 border border-slate-200 rounded"
                        value={newProject.price_max}
                        onChange={(e) => setNewProject({...newProject, price_max: e.target.value})}
                        placeholder="e.g., 15000000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Launch Date</label>
                      <input
                        type="date"
                        className="w-full px-3 py-2 border border-slate-200 rounded"
                        value={newProject.launch_date}
                        onChange={(e) => setNewProject({...newProject, launch_date: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Possession Date</label>
                      <input
                        type="date"
                        className="w-full px-3 py-2 border border-slate-200 rounded"
                        value={newProject.possession_date}
                        onChange={(e) => setNewProject({...newProject, possession_date: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">RERA Number</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-slate-200 rounded"
                        value={newProject.rera_number}
                        onChange={(e) => setNewProject({...newProject, rera_number: e.target.value})}
                        placeholder="e.g., K-RERA/P/2026/001234"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Source URL</label>
                      <input
                        type="url"
                        className="w-full px-3 py-2 border border-slate-200 rounded"
                        value={newProject.source_url}
                        onChange={(e) => setNewProject({...newProject, source_url: e.target.value})}
                        placeholder="https://builder-website.com/project"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-1">Amenities (comma separated)</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-slate-200 rounded"
                        value={newProject.amenities}
                        onChange={(e) => setNewProject({...newProject, amenities: e.target.value})}
                        placeholder="e.g., Swimming Pool, Gym, Club House"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-1">Image URLs (comma separated)</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-slate-200 rounded"
                        value={newProject.images}
                        onChange={(e) => setNewProject({...newProject, images: e.target.value})}
                        placeholder="https://image1.jpg, https://image2.jpg"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-1">Description</label>
                      <textarea
                        rows={3}
                        className="w-full px-3 py-2 border border-slate-200 rounded"
                        value={newProject.description}
                        onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                        placeholder="Project description..."
                      />
                    </div>
                    <div>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={newProject.approved}
                          onChange={(e) => setNewProject({...newProject, approved: e.target.checked})}
                        />
                        <span className="text-sm font-medium">Approve immediately</span>
                      </label>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button type="button" variant="outline" onClick={() => setShowAddProjectForm(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-primary hover:bg-primary/90 text-white">
                        Add Project
                      </Button>
                    </div>
                  </form>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="text-left p-4 font-semibold">Builder</th>
                      <th className="text-left p-4 font-semibold">Project Name</th>
                      <th className="text-left p-4 font-semibold">Location</th>
                      <th className="text-left p-4 font-semibold">Price Range</th>
                      <th className="text-left p-4 font-semibold">Status</th>
                      <th className="text-left p-4 font-semibold">Approved</th>
                      <th className="text-left p-4 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {builderProjects.map((project) => (
                      <tr key={project.project_id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-4 font-medium">{project.builder_name}</td>
                        <td className="p-4">{project.project_name}</td>
                        <td className="p-4">{project.location?.district}</td>
                        <td className="p-4">₹{(project.price_range?.min / 10000000).toFixed(1)}Cr - ₹{(project.price_range?.max / 10000000).toFixed(1)}Cr</td>
                        <td className="p-4 capitalize">{project.status}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            project.approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {project.approved ? 'Yes' : 'Pending'}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex gap-2">
                            {!project.approved && (
                              <Button
                                size="sm"
                                onClick={() => handleApproveProject(project.project_id)}
                                className="bg-green-500 hover:bg-green-600 text-white"
                              >
                                Approve
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteProject(project.project_id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" data-testid="users-content">
            <div className="bg-white rounded-lg border border-slate-100 shadow-sm">
              <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                <h3 className="text-xl font-semibold">All Users ({users.length})</h3>
                <div className="flex gap-2">
                  <Button
                    data-testid="export-users-csv"
                    onClick={() => exportToCSV(users, 'users')}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Export CSV
                  </Button>
                  <Button
                    data-testid="export-users-json"
                    onClick={() => exportToJSON(users, 'users')}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Export JSON
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="text-left p-4 font-semibold">User ID</th>
                      <th className="text-left p-4 font-semibold">Name</th>
                      <th className="text-left p-4 font-semibold">Email</th>
                      <th className="text-left p-4 font-semibold">Phone</th>
                      <th className="text-left p-4 font-semibold">Interest</th>
                      <th className="text-left p-4 font-semibold">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.user_id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-4 text-sm font-mono">{user.user_id}</td>
                        <td className="p-4">{user.name}</td>
                        <td className="p-4">{user.email}</td>
                        <td className="p-4">{user.phone || 'N/A'}</td>
                        <td className="p-4 capitalize">{user.property_interest || 'N/A'}</td>
                        <td className="p-4 capitalize">{user.user_type || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Inquiries Tab */}
          <TabsContent value="inquiries" data-testid="inquiries-content">
            <div className="bg-white rounded-lg border border-slate-100 shadow-sm">
              <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                <h3 className="text-xl font-semibold">All Inquiries ({inquiries.length})</h3>
                <div className="flex gap-2">
                  <Button
                    data-testid="export-inquiries-csv"
                    onClick={() => exportToCSV(inquiries, 'inquiries')}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Export CSV
                  </Button>
                  <Button
                    data-testid="export-inquiries-json"
                    onClick={() => exportToJSON(inquiries, 'inquiries')}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Export JSON
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="text-left p-4 font-semibold">Inquiry ID</th>
                      <th className="text-left p-4 font-semibold">Property ID</th>
                      <th className="text-left p-4 font-semibold">Name</th>
                      <th className="text-left p-4 font-semibold">Email</th>
                      <th className="text-left p-4 font-semibold">Phone</th>
                      <th className="text-left p-4 font-semibold">Message</th>
                      <th className="text-left p-4 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inquiries.map((inquiry) => (
                      <tr key={inquiry.inquiry_id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-4 text-sm font-mono">{inquiry.inquiry_id}</td>
                        <td className="p-4 text-sm font-mono">{inquiry.property_id}</td>
                        <td className="p-4">{inquiry.user_name}</td>
                        <td className="p-4">{inquiry.user_email}</td>
                        <td className="p-4">{inquiry.user_phone || 'N/A'}</td>
                        <td className="p-4 max-w-xs truncate">{inquiry.message}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            inquiry.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {inquiry.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
